import type { ZoneGeoJsonPolygon } from './pricing.schemas';

type Position = [number, number];

const cross = (a: Position, b: Position, c: Position) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);

function isOnSegment(a: Position, b: Position, p: Position): boolean {
  return Math.min(a[0], b[0]) <= p[0] && p[0] <= Math.max(a[0], b[0]) && Math.min(a[1], b[1]) <= p[1] && p[1] <= Math.max(a[1], b[1]);
}

/** Whether segment a–b meets segment c–d anywhere, a shared end or an overlap included. */
function segmentsMeet(a: Position, b: Position, c: Position, d: Position): boolean {
  const d1 = cross(c, d, a);
  const d2 = cross(c, d, b);
  const d3 = cross(a, b, c);
  const d4 = cross(a, b, d);
  if (d1 * d2 < 0 && d3 * d4 < 0) return true;
  return (d1 === 0 && isOnSegment(c, d, a)) || (d2 === 0 && isOnSegment(c, d, b)) || (d3 === 0 && isOnSegment(a, b, c)) || (d4 === 0 && isOnSegment(a, b, d));
}

function hasSelfIntersection(ring: Position[]): boolean {
  const edges = ring.length - 1;
  for (let i = 0; i < edges; i++) {
    // Edges that share a corner always meet there; the first and last edge share the closing corner.
    for (let j = i + 2; j < edges; j++) {
      if (i === 0 && j === edges - 1) continue;
      if (segmentsMeet(ring[i], ring[i + 1], ring[j], ring[j + 1])) return true;
    }
  }
  return false;
}

/**
 * Why a drawn boundary cannot be a geofence, or null when it can. One ring only (a geofence has no holes), at least three
 * different corners, and no edge crossing another: a figure-8 has no inside, so "is the truck in it" would have no answer.
 * The API's pipe and the web editor's inline error both call this, so they cannot disagree.
 */
export function geofencePolygonProblem(polygon: ZoneGeoJsonPolygon): string | null {
  if (polygon.coordinates.length !== 1) return 'A geofence is one outline, without holes';
  const ring = polygon.coordinates[0];
  const corners = new Set(ring.slice(0, -1).map(([lng, lat]) => `${lng},${lat}`));
  if (corners.size < 3) return 'A geofence needs at least three different corners';
  if (hasSelfIntersection(ring)) return 'The outline crosses itself';
  return null;
}
